'use client'

import { cn } from '@/utilities/cn'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { useState } from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded text-sm uppercase weight-[700] font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        clear: '',
        default: 'h-10 px-4 py-2 rounded-full',
        icon: 'h-10 w-10',
        lg: 'h-12 rounded-full px-8',
        sm: 'h-6 rounded-full px-3 text-sm',
      },
      variant: {
        default:
          'bg-primary font-bold text-white hover:bg-white hover:text-primary border border-primary',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost: 'hover:bg-card hover:text-primary-foreground',
        link: 'text-primary items-start justify-start underline-offset-4 hover:underline',
        black:
          'bg-foreground text-primary-background hover:border-foreground border border-foreground',
        outline: 'border border-primary hover:bg-primary hover:text-white text-primary',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        success: 'bg-green-600 text-white hover:bg-green-700 border border-green-600',
        danger: 'bg-red-600 text-white hover:bg-red-700 border border-red-600',
        warning: 'bg-amber-600 text-white hover:bg-amber-700 border border-amber-600',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = 'Button'

const ToggleButton = React.forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'> & { pressed: boolean }
>(({ pressed, ...props }, ref) => {
  return <Button variant={pressed ? 'default' : 'outline'} ref={ref} {...props} />
})
ToggleButton.displayName = 'ToggleButton'

export { Button, ToggleButton, buttonVariants }
